import { useState } from 'react';
import { SEO } from '../../../utils/seo';

export default function LandIntelligencePage() {
  const [activeModule, setActiveModule] = useState<number | null>(null);

  const modules = [
    {
      title: 'Timber Inventory & Forest Health',
      description: 'Stand mapping, density estimates, and health indicators across forested acreage.',
      riskAvoided: 'Overpaying for timber or mistiming harvest decisions.',
      image: 'https://public.readdy.ai/ai/img_res/a60b54cbaab351ab3cc21c40e78141b7.jpg',
      icon: 'ri-plant-line'
    },
    {
      title: 'Terrain, Drainage & Access',
      description: 'High-resolution terrain models highlighting slope, drainage paths, and access routes.',
      riskAvoided: 'Hidden site constraints that inflate operating or development costs.',
      image: 'https://readdy.ai/api/search-image?query=Professional%20topographic%20terrain%20map%20with%20elevation%20contours%2C%20drainage%20patterns%20visible%2C%20land%20survey%20perspective%2C%20natural%20earth%20tones%2C%20clear%20detailed%20mapping%2C%20realistic%20cartography%20style%2C%20showing%20slopes%20and%20access%20routes&width=800&height=600&seq=terrain-drainage-module&orientation=landscape',
      icon: 'ri-map-2-line'
    },
    {
      title: 'Infrastructure & Asset Review',
      description: 'Inspection of roads, bridges, culverts, towers, and on-site structures.',
      riskAvoided: 'Unexpected repair or replacement costs after acquisition.',
      image: 'https://readdy.ai/api/search-image?query=Professional%20drone%20inspecting%20rural%20infrastructure%2C%20country%20road%20with%20bridge%20and%20culvert%2C%20aerial%20inspection%20perspective%2C%20natural%20daylight%2C%20realistic%20photography%20style%2C%20showing%20rural%20land%20access%20infrastructure%20clearly&width=800&height=600&seq=infrastructure-review-module&orientation=landscape',
      icon: 'ri-road-map-line'
    },
    {
      title: 'Environmental & Compliance Flags',
      description: 'Early indicators of erosion, wet areas, and regulated land.',
      riskAvoided: 'Permitting delays and land-use restrictions discovered too late.',
      image: 'https://readdy.ai/api/search-image?query=Professional%20aerial%20drone%20mapping%20land%20with%20water%20features%20and%20wetlands%2C%20environmental%20survey%20perspective%2C%20natural%20landscape%20with%20streams%20and%20wet%20areas%2C%20realistic%20photography%20style%2C%20clear%20daylight%20showing%20land%20use%20patterns&width=800&height=600&seq=environmental-compliance-module&orientation=landscape',
      icon: 'ri-leaf-line'
    },
    {
      title: 'Executive Summary Report',
      description: 'Clear, buyer-ready documentation of findings and risks.',
      riskAvoided: 'Weak leverage during negotiation or underwriting.',
      image: 'https://readdy.ai/api/search-image?query=Professional%20executive%20report%20document%20layout%2C%20clean%20business%20report%20design%2C%20land%20analysis%20summary%20with%20maps%20and%20data%2C%20premium%20professional%20style%2C%20organized%20information%20presentation%2C%20realistic%20business%20document%20photography&width=800&height=600&seq=executive-report-module&orientation=landscape',
      icon: 'ri-file-text-line'
    }
  ];

  const audiences = [
    { title: 'Land buyers and investors', icon: 'ri-building-line' },
    { title: 'Timber and ag funds', icon: 'ri-funds-line' },
    { title: 'Developers and operators', icon: 'ri-tools-line' },
    { title: 'Family offices', icon: 'ri-home-office-line' },
    { title: 'Insurance and risk teams', icon: 'ri-shield-check-line' }
  ];

  const scrollToModules = () => {
    document.getElementById('modules-section')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <>
      <SEO
        title="Land Intelligence & Due Diligence | Know the Asset Before You Buy"
        description="Drone-based land intelligence for buyers, investors, and landowners. Timber, terrain, infrastructure, and risk documentation before acquisition."
        keywords="Land intelligence services, Land due diligence drone survey, Pre-acquisition land analysis, Timber and land valuation drone"
      />

      {/* Hero Section */}
      <section className="relative min-h-[600px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Professional%20wide%20aerial%20photography%20of%20large%20land%20parcel%2C%20expansive%20rural%20property%20from%20above%2C%20natural%20landscape%20with%20mixed%20terrain%2C%20realistic%20drone%20photography%20style%2C%20golden%20hour%20lighting%2C%20clear%20detailed%20view%20showing%20property%20boundaries%20and%20features&width=1920&height=1080&seq=land-intelligence-hero&orientation=landscape"
            alt="Aerial view of land parcel"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/30"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 py-24 text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">
            Know the Land Before You Own It
          </h1>
          <p className="text-xl md:text-2xl text-white/90 mb-10 max-w-3xl mx-auto leading-relaxed">
            Most land risks don't show up on a walkthrough. Land intelligence replaces assumptions with documented reality before capital is committed.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={scrollToModules}
              className="px-8 py-4 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-lg transition-all duration-300 shadow-lg hover:shadow-xl"
            >
              See What's Included
            </button>
            <a
              href="/instant-quote"
              className="px-8 py-4 bg-white/10 hover:bg-white/20 text-white font-semibold rounded-lg transition-all duration-300 backdrop-blur-sm border border-white/30"
            >
              Request a Consultation
            </a>
          </div>
        </div>
      </section>

      {/* Loss-Aversion Intro */}
      <section className="py-20 bg-stone-50">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold text-stone-900 mb-8">
            Bad Land Data Is Expensive
          </h2>
          <div className="space-y-4 text-lg text-stone-700 leading-relaxed">
            <p>Timber gets mispriced.</p>
            <p>Drainage problems surface later.</p>
            <p>Access fails under equipment.</p>
            <p>Infrastructure issues appear after closing.</p>
            <p className="text-xl font-semibold text-stone-900 mt-8 pt-8 border-t border-stone-300">
              Land intelligence exists to surface risk early, while decisions are still reversible.
            </p>
          </div>
        </div>
      </section>

      {/* What's Included - Bundled Modules */}
      <section id="modules-section" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-4xl font-bold text-stone-900 mb-4 text-center">
            What's Included
          </h2>
          <p className="text-xl text-stone-600 mb-16 text-center max-w-3xl mx-auto">
            Comprehensive land intelligence delivered as bundled modules
          </p>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {modules.map((module, index) => (
              <div
                key={index}
                className="bg-stone-50 rounded-xl overflow-hidden hover:shadow-xl transition-all duration-300 cursor-pointer border border-stone-200"
                onClick={() => setActiveModule(activeModule === index ? null : index)}
              >
                <div className="relative h-56 overflow-hidden">
                  <img
                    src={module.image}
                    alt={module.title}
                    className="w-full h-full object-cover object-center transition-transform duration-500 hover:scale-105"
                  />
                  <div className="absolute top-4 left-4 w-12 h-12 bg-emerald-600 rounded-lg flex items-center justify-center">
                    <i className={`${module.icon} text-2xl text-white`}></i>
                  </div>
                </div>
                
                <div className="p-6">
                  <h3 className="text-xl font-bold text-stone-900 mb-3">
                    {module.title}
                  </h3>
                  <p className="text-stone-700 mb-4 leading-relaxed">
                    {module.description}
                  </p>
                  
                  <div className="pt-4 border-t border-stone-300">
                    <p className="text-sm font-semibold text-stone-600 mb-2">
                      Risk avoided:
                    </p>
                    <p className="text-stone-800 leading-relaxed">
                      {module.riskAvoided}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who This Is For */}
      <section className="py-20 bg-stone-900 text-white">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-4xl font-bold mb-12 text-center">
            Who This Is For
          </h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-6 mb-12">
            {audiences.map((audience, index) => (
              <div
                key={index}
                className="bg-white/5 backdrop-blur-sm rounded-lg p-6 text-center hover:bg-white/10 transition-all duration-300 border border-white/10"
              >
                <div className="w-16 h-16 bg-emerald-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className={`${audience.icon} text-3xl text-emerald-400`}></i>
                </div>
                <p className="text-white font-medium leading-snug">
                  {audience.title}
                </p>
              </div>
            ))}
          </div>

          <p className="text-xl text-center text-white/90 font-medium">
            If the decision involves serious capital, this pays for itself.
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative min-h-[500px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Professional%20expansive%20land%20photography%20at%20golden%20hour%2C%20wide%20open%20rural%20property%20landscape%2C%20warm%20sunset%20lighting%2C%20natural%20earth%20tones%2C%20peaceful%20countryside%20vista%2C%20realistic%20photography%20style%2C%20clear%20detailed%20view%20of%20pristine%20land%20parcel&width=1920&height=1080&seq=land-intelligence-cta&orientation=landscape"
            alt="Expansive land at golden hour"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/50 to-black/30"></div>
        </div>
        
        <div className="relative z-10 max-w-4xl mx-auto px-6 py-20 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 leading-tight">
            Measure the Risk Before You Buy the Land
          </h2>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mt-10">
            <a
              href="/instant-quote"
              className="px-8 py-4 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-lg transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap"
            >
              Request a Consultation
            </a>
            <a
              href="/land-intelligence-pricing"
              className="px-8 py-4 bg-white/10 hover:bg-white/20 text-white font-semibold rounded-lg transition-all duration-300 backdrop-blur-sm border border-white/30 whitespace-nowrap"
            >
              Get a Price Estimate
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
